import {
  gql,
  OperationVariables,
  QueryHookOptions,
  QueryResult,
  useQuery,
} from '@apollo/client';
import type { ConsolidatedEntityTriageReport } from '@investigator/shared/generated/graphql';

/**
 * Query response type for getConsolidatedEntityTriageReports
 * Note: Backend returns an array, we take the first item
 */
export interface IGetConsolidatedEntityTriageReports {
  getConsolidatedEntityTriageReports: ConsolidatedEntityTriageReport[] | null;
}

/**
 * GraphQL query for fetching consolidated entity triage reports
 * This query retrieves AI-generated triage analysis for specific entities.
 *
 * Query supports two modes:
 * 1. By report_id (tenant_report_id_list) - for fetching existing reports
 * 2. By entity_id (tenant_entity_input) - for fetching reports by entity
 *
 * @see ConsolidatedEntityTriageReport for current schema structure
 * @see apps/investigator/src/features/entities/types/agenticTriageReport.ts for local types
 */
export const GET_CONSOLIDATED_ENTITY_TRIAGE_REPORT = gql`
  query getConsolidatedEntityTriageReports(
    $tenantReportIdList: [TenantReportIdInput!]
    $tenantEntityInput: TenantEntityIdInput
  ) {
    getConsolidatedEntityTriageReports(
      tenant_report_id_list: $tenantReportIdList
      tenant_entity_input: $tenantEntityInput
    ) {
      report_id
      tenant
      entity_id
      investigation_id
      investigation_start_timestamp
      investigation_end_timestamp
      created_at
      entity_info {
        summary
      }
      analysis_summary {
        verdict
        description
        details
      }
      detections_info {
        description
      }
      playbook {
        assessment_summary {
          step_name
          summary
        }
      }
      key_findings_info {
        summary
        key_findings {
          title
          description
          justification
          logs_query
          evidences {
            agent
            source
            detection_id
            entity_triage_id
            tool_name
          }
        }
      }
      recommended_actions_info {
        to_isolate_entity
        recommended_actions
      }
    }
  }
`;

/**
 * Hook for querying consolidated entity triage report
 *
 * @example Using report_id:
 * ```tsx
 * const { data, loading, error } = useGetConsolidatedEntityTriageReport({
 *   variables: {
 *     tenantReportIdList: [{ tenant: 'my-tenant', report_id: 'report-123' }],
 *   },
 *   skip: !reportId || !agenticTriageEnabled,
 * });
 * ```
 *
 * @example Using entity_id:
 * ```tsx
 * const { data, loading, error } = useGetConsolidatedEntityTriageReport({
 *   variables: {
 *     tenantEntityInput: {
 *       tenant: 'my-tenant',
 *       entity_id: 'entity-456',
 *       start: 1234567890,
 *       end: 1234567999,
 *     },
 *   },
 *   skip: !entityId || !agenticTriageEnabled,
 * });
 * ```
 *
 * // Access the first report from the array
 * const report = data?.getConsolidatedEntityTriageReports?.[0];
 */
export const useGetConsolidatedEntityTriageReport = (
  options: QueryHookOptions<
    IGetConsolidatedEntityTriageReports,
    OperationVariables
  >,
): QueryResult<IGetConsolidatedEntityTriageReports, OperationVariables> => {
  return useQuery<IGetConsolidatedEntityTriageReports>(
    GET_CONSOLIDATED_ENTITY_TRIAGE_REPORT,
    options,
  );
};
